import React from 'react';

/*
 * The application's error boundary.
 *
 * By default it renders the "Something went wrong" card that a signed-in page
 * wants. Two optional props let a caller change that without a second boundary:
 * `fallback` replaces the card (pass null to render nothing) and `onError` is
 * told, which is how a caller can unmount the failed subtree entirely.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    console.error('[ErrorBoundary] Caught error:', error);
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.props.onError?.(error, errorInfo);

    console.error('[ErrorBoundary] Error details:', {
      error: error,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    });
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      /* A caller may supply what to show instead, including nothing at all.
       * The marketing homepage does exactly that: a live product preview that
       * throws leaves the static poster it was covering, and a marketing page
       * never shows an application error. */
      if (this.props.fallback !== undefined) return this.props.fallback;

      return (
        <div className="p-6">
          <div className="card">
            <h1 className="text-xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-gray-400 mb-4">The application encountered an error. Please refresh the page.</p>
            {this.state.error && (
              <details className="text-sm text-gray-500">
                <summary className="cursor-pointer">Error details</summary>
                <pre className="mt-2 p-2 bg-gray-800 rounded overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;